import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import RichTextColorPicker from "./RichTextColorPicker";
import Title from "../../../../components/title";
import { LoadingTable } from "../../../../components/Loading/LoadingTable";
import paginaLinksService from "../../../../services/paginaLinks";
import { PermissionsPage } from "../../../../util/permissionsPage";

const newButton = () => ({
  _key: Math.random().toString(36).slice(2),
  id: null,
  label: "",
  href: "",
  icon: "",
  _iconFile: null,
  bg_color: "#22C55E",
  text_color: "#ffffff",
  _toDelete: false,
});

const newCarouselItem = () => ({
  _key: Math.random().toString(36).slice(2),
  id: null,
  image_url: "",
  _file: null,
  _toDelete: false,
});

export const CreatePaginaLinks = () => {
  const history = useHistory();
  const params = useParams();
  const isEdit = !!params?.id;
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [heroImageFile, setHeroImageFile] = useState(null);

  const [buttons, setButtons] = useState([newButton()]);
  const [carousel, setCarousel] = useState([newCarouselItem()]);

  const [headlineState, setHeadlineState] = useState(EditorState.createEmpty());
  const [subheadlineState, setSubheadlineState] = useState(EditorState.createEmpty());
  const [socialProofState, setSocialProofState] = useState(EditorState.createEmpty());

  const htmlToEditorState = (html) => {
    if (!html) return EditorState.createEmpty();
    const { contentBlocks, entityMap } = htmlToDraft(html);
    const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
    return EditorState.createWithContent(contentState);
  };

  const editorStateToHtml = (state) =>
    draftToHtml(convertToRaw(state.getCurrentContent()));

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      slug: "",
      background_color: "#111111",
      bar_color: "#b00055",
      header_text: "",
      hero_image_url: "",
    },
  });

  const goBackList = () => history.push("/links/paginas/");

  const getInfo = async () => {
    setIsLoading(true);
    try {
      const response = await paginaLinksService.buscarPorId(params.id);
      const page = response.data;
      reset({
        name: page.name,
        slug: page.slug,
        background_color: page.background_color ?? "#111111",
        bar_color: page.bar_color ?? "#b00055",
        header_text: page.header_text,
        hero_image_url: page.hero_image_url,
      });
      setHeadlineState(htmlToEditorState(page.headline));
      setSubheadlineState(htmlToEditorState(page.subheadline));
      setSocialProofState(htmlToEditorState(page.social_proof));
      setButtons(
        (page.buttons ?? []).map((b) => ({
          _key: String(b.id),
          id: b.id,
          label: b.label,
          href: b.href,
          icon: b.icon,
          _iconFile: null,
          bg_color: b.bg_color,
          text_color: b.text_color,
          _toDelete: false,
        }))
      );
      setCarousel(
        (page.carouselImages ?? []).map((img) => ({
          _key: String(img.id),
          id: img.id,
          image_url: img.image_url,
          _toDelete: false,
        }))
      );
    } catch (error) {
      toast.error("Página não encontrada");
      goBackList();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      getInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.id]);

  // ── Button helpers ────────────────────────────────────────────────────────

  const addButton = () => setButtons((prev) => [...prev, newButton()]);

  const removeButton = (key) =>
    setButtons((prev) =>
      prev.map((b) => (b._key === key ? { ...b, _toDelete: true } : b))
    );

  const updateButton = (key, field, value) =>
    setButtons((prev) =>
      prev.map((b) => (b._key === key ? { ...b, [field]: value } : b))
    );

  const updateButtonFields = (key, fields) =>
    setButtons((prev) =>
      prev.map((b) => (b._key === key ? { ...b, ...fields } : b))
    );

  // ── Carousel helpers ──────────────────────────────────────────────────────

  const addCarouselItem = () => setCarousel((prev) => [...prev, newCarouselItem()]);

  const removeCarouselItem = (key) =>
    setCarousel((prev) =>
      prev.map((c) => (c._key === key ? { ...c, _toDelete: true } : c))
    );

  const updateCarouselFile = (key, file) =>
    setCarousel((prev) =>
      prev.map((c) => (c._key === key ? { ...c, _file: file } : c))
    );

  // ── Submit ────────────────────────────────────────────────────────────────

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const pageId = params?.id ? Number(params.id) : null;

      data.headline = editorStateToHtml(headlineState);
      data.subheadline = editorStateToHtml(subheadlineState);
      data.social_proof = editorStateToHtml(socialProofState);

      const toSaveButtons = buttons.filter((b) => !b._toDelete);
      const toDeleteButtons = buttons.filter((b) => b._toDelete && b.id !== null);

      // Novos arquivos de carousel (itens sem id com _file)
      const newCarouselFiles = carousel
        .filter((c) => !c._toDelete && c.id === null && c._file)
        .map((c) => c._file);
      const toDeleteCarousel = carousel.filter((c) => c._toDelete && c.id !== null);

      if (isEdit) {
        await paginaLinksService.atualizar(
          pageId,
          data,
          heroImageFile || null,
          toSaveButtons,
          newCarouselFiles,
          toDeleteButtons,
          toDeleteCarousel,
        );
      } else {
        await paginaLinksService.criar(data, heroImageFile || null, toSaveButtons, newCarouselFiles);
      }

      toast.success("Página salva com sucesso");
      goBackList();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.message;
      toast.error(errorMessage ?? "Erro ao salvar página");
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box width="100%" display="flex" justifyContent="center" alignItems="center" height="90vh">
        <LoadingTable />
      </Box>
    );
  }

  const visibleButtons = buttons.filter((b) => !b._toDelete);
  const visibleCarousel = carousel.filter((c) => !c._toDelete);

  return (
    <>
      {PermissionsPage("paginaLinks") && (
        <>
          <Box ml={2}>
            <Title title={isEdit ? "Editar Página de Links" : "Criar Página de Links"} />
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ px: 3, pb: 4 }}
          >
            <Stack spacing={3}>

              {/* ── Identificação ────────────────────────────────────── */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                  Identificação
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...register("name", { required: true })}
                      label="Nome da página"
                      size="small"
                      fullWidth
                      error={!!errors.name}
                      helperText="Nome para identificação interna — não aparece no site"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...register("slug", { required: true })}
                      label="Slug"
                      size="small"
                      fullWidth
                      disabled={isEdit}
                      error={!!errors.slug}
                      helperText={
                        isEdit
                          ? "O slug não pode ser alterado após a criação"
                          : "Identificador único da página (ex: bebe-mama)"
                      }
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* ── Textos ───────────────────────────────────────────── */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                  Textos
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    {...register("header_text", { required: true })}
                    label="Texto do cabeçalho"
                    size="small"
                    fullWidth
                    error={!!errors.header_text}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
                      Headline
                    </Typography>
                    <Box
                      sx={{
                        border: "1px solid rgba(0,0,0,0.23)",
                        borderRadius: 1,
                        "& .rdw-editor-toolbar": { borderBottom: "1px solid rgba(0,0,0,0.12)", borderTop: "none", borderLeft: "none", borderRight: "none", mb: 0 },
                        "& .rdw-editor-main": { px: 1.5, minHeight: 56 },
                      }}
                    >
                      <Editor
                        editorState={headlineState}
                        onEditorStateChange={setHeadlineState}
                        toolbar={{
                          options: ["inline", "fontSize", "colorPicker"],
                          inline: { options: ["bold", "italic", "underline", "strikethrough"] },
                          colorPicker: { component: RichTextColorPicker },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
                      Subheadline
                    </Typography>
                    <Box
                      sx={{
                        border: "1px solid rgba(0,0,0,0.23)",
                        borderRadius: 1,
                        "& .rdw-editor-toolbar": { borderBottom: "1px solid rgba(0,0,0,0.12)", borderTop: "none", borderLeft: "none", borderRight: "none", mb: 0 },
                        "& .rdw-editor-main": { px: 1.5, minHeight: 72 },
                      }}
                    >
                      <Editor
                        editorState={subheadlineState}
                        onEditorStateChange={setSubheadlineState}
                        toolbar={{
                          options: ["inline", "fontSize", "colorPicker"],
                          inline: { options: ["bold", "italic", "underline", "strikethrough"] },
                          colorPicker: { component: RichTextColorPicker },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
                      Prova social
                    </Typography>
                    <Box
                      sx={{
                        border: "1px solid rgba(0,0,0,0.23)",
                        borderRadius: 1,
                        "& .rdw-editor-toolbar": { borderBottom: "1px solid rgba(0,0,0,0.12)", borderTop: "none", borderLeft: "none", borderRight: "none", mb: 0 },
                        "& .rdw-editor-main": { px: 1.5, minHeight: 56 },
                      }}
                    >
                      <Editor
                        editorState={socialProofState}
                        onEditorStateChange={setSocialProofState}
                        toolbar={{
                          options: ["inline", "fontSize", "colorPicker"],
                          inline: { options: ["bold", "italic", "underline", "strikethrough"] },
                          colorPicker: { component: RichTextColorPicker },
                        }}
                      />
                    </Box>
                  </Box>
                </Stack>
              </Paper>

              {/* ── Imagens ──────────────────────────────────────────── */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                  Imagens
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Imagem hero
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Dimensão recomendada: 1500×2160px (retrato). A imagem será exibida em largura total — certifique-se de que o recorte está correto antes de enviar.
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      disabled={isSubmitting}
                    >
                      {heroImageFile ? "Trocar arquivo" : "Selecionar arquivo"}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setHeroImageFile(file);
                            setValue("hero_image_url", file.name);
                          }
                        }}
                      />
                    </Button>
                    {(heroImageFile || watch("hero_image_url")) && (
                      <Box mt={1} display="flex" alignItems="flex-start" gap={2}>
                        <Box
                          component="img"
                          src={heroImageFile ? URL.createObjectURL(heroImageFile) : watch("hero_image_url")}
                          alt="Preview hero"
                          sx={{ maxWidth: 120, maxHeight: 180, objectFit: "contain", border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                        />
                        {heroImageFile && (
                          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                            {heroImageFile.name}
                          </Typography>
                        )}
                      </Box>
                    )}
                    <input type="hidden" {...register("hero_image_url", { required: true })} />
                    {errors.hero_image_url && (
                      <Typography variant="caption" color="error" display="block" mt={0.5}>
                        Selecione uma imagem hero
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Paper>

              {/* ── Cores ────────────────────────────────────────────── */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                  Cores
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <input
                        type="color"
                        value={watch("background_color") || "#000000"}
                        onChange={(e) => setValue("background_color", e.target.value)}
                        style={{ width: 32, height: 32, border: "none", padding: 0, cursor: "pointer", borderRadius: 4 }}
                        title="Cor de fundo"
                      />
                      <TextField
                        label="Cor de fundo"
                        size="small"
                        fullWidth
                        value={watch("background_color") || ""}
                        onChange={(e) => setValue("background_color", e.target.value)}
                        error={!!errors.background_color}
                      />
                      <input type="hidden" {...register("background_color", { required: true })} />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <input
                        type="color"
                        value={watch("bar_color") || "#000000"}
                        onChange={(e) => setValue("bar_color", e.target.value)}
                        style={{ width: 32, height: 32, border: "none", padding: 0, cursor: "pointer", borderRadius: 4 }}
                        title="Cor da barra superior"
                      />
                      <TextField
                        label="Cor da barra superior"
                        size="small"
                        fullWidth
                        value={watch("bar_color") || ""}
                        onChange={(e) => setValue("bar_color", e.target.value)}
                      />
                      <input type="hidden" {...register("bar_color")} />
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {/* ── Botões ───────────────────────────────────────────── */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Botões ({visibleButtons.length})
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addButton}
                    variant="outlined"
                    disabled={isSubmitting}
                  >
                    Adicionar botão
                  </Button>
                </Box>

                {visibleButtons.length === 0 && (
                  <Typography color="text.secondary" variant="body2">
                    Nenhum botão adicionado.
                  </Typography>
                )}

                <Stack spacing={2}>
                  {visibleButtons.map((btn, idx) => (
                    <Paper key={btn._key} variant="elevation" elevation={1} sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="caption" color="text.secondary">
                          Botão {idx + 1}
                        </Typography>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => removeButton(btn._key)}
                          disabled={isSubmitting}
                        >
                          Remover botão
                        </Button>
                      </Box>
                      <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Rótulo"
                            size="small"
                            fullWidth
                            value={btn.label}
                            onChange={(e) => updateButton(btn._key, "label", e.target.value)}
                            disabled={isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            label="URL (href)"
                            size="small"
                            fullWidth
                            value={btn.href}
                            onChange={(e) => updateButton(btn._key, "href", e.target.value)}
                            disabled={isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          {btn._iconFile || btn.icon ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                px: 1,
                                py: 0.5,
                                height: 40,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1,
                                  bgcolor: btn.bg_color || "#22C55E",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <Box
                                  component="img"
                                  src={btn._iconFile ? URL.createObjectURL(btn._iconFile) : btn.icon}
                                  alt="preview"
                                  sx={{ width: 22, height: 22, objectFit: "contain" }}
                                />
                              </Box>
                              <Box flex={1} />
                              <Tooltip title="Editar ícone">
                                <IconButton
                                  size="small"
                                  component="label"
                                  disabled={isSubmitting}
                                >
                                  <EditIcon fontSize="small" />
                                  <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) updateButton(btn._key, "_iconFile", file);
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remover ícone">
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={isSubmitting}
                                  onClick={() => updateButtonFields(btn._key, { icon: null, _iconFile: null })}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Button
                              variant="outlined"
                              component="label"
                              size="small"
                              disabled={isSubmitting}
                              fullWidth
                              sx={{ height: 40 }}
                            >
                              Selecionar ícone
                              <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) updateButton(btn._key, "_iconFile", file);
                                }}
                              />
                            </Button>
                          )}
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <input
                              type="color"
                              value={btn.bg_color}
                              onChange={(e) => updateButton(btn._key, "bg_color", e.target.value)}
                              style={{ width: 32, height: 32, border: "none", padding: 0, cursor: "pointer", borderRadius: 4 }}
                              disabled={isSubmitting}
                              title="Cor de fundo do botão"
                            />
                            <TextField
                              label="Cor de fundo"
                              size="small"
                              fullWidth
                              value={btn.bg_color}
                              onChange={(e) => updateButton(btn._key, "bg_color", e.target.value)}
                              disabled={isSubmitting}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <input
                              type="color"
                              value={btn.text_color}
                              onChange={(e) => updateButton(btn._key, "text_color", e.target.value)}
                              style={{ width: 32, height: 32, border: "none", padding: 0, cursor: "pointer", borderRadius: 4 }}
                              disabled={isSubmitting}
                              title="Cor do texto"
                            />
                            <TextField
                              label="Cor do texto"
                              size="small"
                              fullWidth
                              value={btn.text_color}
                              onChange={(e) => updateButton(btn._key, "text_color", e.target.value)}
                              disabled={isSubmitting}
                            />
                          </Stack>
                        </Grid>

                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </Paper>

              {/* ── Carrossel ────────────────────────────────────────── */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Imagens do carrossel ({visibleCarousel.length})
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addCarouselItem}
                    variant="outlined"
                    disabled={isSubmitting}
                  >
                    Adicionar imagem
                  </Button>
                </Box>

                {visibleCarousel.length === 0 && (
                  <Typography color="text.secondary" variant="body2">
                    Nenhuma imagem adicionada.
                  </Typography>
                )}

                <Stack spacing={0} divider={<Divider flexItem />}>
                  {visibleCarousel.map((item, idx) => (
                    <Box key={item._key} display="flex" gap={1} alignItems="flex-start" py={1.5}>
                      {item.id !== null ? (
                        <Box sx={{ flex: 1 }}>
                          <Box
                            component="img"
                            src={item.image_url}
                            alt={`Imagem ${idx + 1}`}
                            sx={{ maxHeight: 60, maxWidth: 120, objectFit: "contain", border: "1px solid", borderColor: "divider", borderRadius: 1, display: "block", mb: 0.5 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Imagem {idx + 1} — dimensão recomendada: 500×715px (retrato). Remova e adicione novamente para alterar.
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Imagem {idx + 1} — dimensão recomendada: 500×715px (retrato).
                          </Typography>
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            disabled={isSubmitting}
                          >
                            {item._file ? "Trocar arquivo" : "Selecionar arquivo"}
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) updateCarouselFile(item._key, file);
                              }}
                            />
                          </Button>
                          {item._file && (
                            <Box mt={0.5} display="flex" alignItems="center" gap={1}>
                              <Box
                                component="img"
                                src={URL.createObjectURL(item._file)}
                                alt="preview"
                                sx={{ maxHeight: 50, maxWidth: 80, objectFit: "contain", border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {item._file.name}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      <Tooltip title="Remover imagem">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeCarouselItem(item._key)}
                          disabled={isSubmitting}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Stack>
              </Paper>

              <Divider />

              {/* ── Ações ────────────────────────────────────────────── */}
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Salvando..." : "Salvar"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={goBackList}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
              </Box>

            </Stack>
          </Box>
        </>
      )}
    </>
  );
};
